import {
  ArrowCircleDownIcon,
  ArrowCircleUpIcon,
  BankIcon,
  BatteryWarningIcon,
  BellIcon,
  CheckCircleIcon,
  ChatsCircleIcon,
  ClockCountdownIcon,
  CreditCardIcon,
  DownloadSimpleIcon,
  EnvelopeSimpleIcon,
  EyeIcon,
  GaugeIcon,
  GiftIcon,
  HandWavingIcon,
  LifebuoyIcon,
  LockIcon,
  MegaphoneIcon,
  MinusCircleIcon,
  PercentIcon,
  PlusCircleIcon,
  PrinterIcon,
  SealCheckIcon,
  ShareNetworkIcon,
  ShieldCheckIcon,
  ShieldWarningIcon,
  TranslateIcon,
  UserCircleIcon,
  UserPlusIcon,
  UsersIcon,
  WarningCircleIcon,
  XCircleIcon,
} from "@phosphor-icons/react";
import type { Icon } from "@phosphor-icons/react";
import type { NotificationCategory } from "@reactive-resume/dto";

/**
 * AFFICHAGE DES ÉVÉNEMENTS (client) — miroir du registry serveur.
 * Icône + palette par type ; un type inconnu retombe sur le style générique
 * "bell". Ajouter un type serveur → ajouter son entrée ici.
 */

export type EventDisplay = {
  icon: Icon;
  /** Classes tailwind de la pastille d'icône. */
  className: string;
  label: string;
};

const GENERIC: EventDisplay = {
  icon: BellIcon,
  className: "bg-primary/10 text-primary",
  label: "Notification",
};

export const EVENT_DISPLAY: Record<string, EventDisplay> = {
  PAYMENT_SUCCESS: {
    icon: CreditCardIcon,
    className: "bg-emerald-500/10 text-emerald-600 ",
    label: "Paiement",
  },
  PAYMENT_FAILED: {
    icon: WarningCircleIcon,
    className: "bg-red-500/10 text-red-600 ",
    label: "Paiement",
  },
  SOUSCRIPTION_ACTIVATED: {
    icon: SealCheckIcon,
    className: "bg-indigo-500/10 text-indigo-600 ",
    label: "Pack",
  },
  SOUSCRIPTION_EXPIRING: {
    icon: ClockCountdownIcon,
    className: "bg-amber-500/10 text-amber-600 ",
    label: "Pack",
  },
  SOUSCRIPTION_EXPIRED: {
    icon: WarningCircleIcon,
    className: "bg-red-500/10 text-red-600 ",
    label: "Pack",
  },
  CREDIT_GRANTED: {
    icon: PlusCircleIcon,
    className: "bg-emerald-500/10 text-emerald-600 ",
    label: "Crédits",
  },
  CREDIT_LOW: {
    icon: BatteryWarningIcon,
    className: "bg-amber-500/10 text-amber-600 ",
    label: "Crédits",
  },
  CREDIT_DEPLETED: {
    icon: BatteryWarningIcon,
    className: "bg-red-500/10 text-red-600 ",
    label: "Crédits",
  },
  CREDIT_CONSUMED: {
    icon: MinusCircleIcon,
    className: "bg-slate-500/10 text-slate-600 ",
    label: "Crédits",
  },
  WALLET_CREDIT: {
    icon: ArrowCircleDownIcon,
    className: "bg-emerald-500/10 text-emerald-600 ",
    label: "Portefeuille",
  },
  WALLET_DEBIT: {
    icon: ArrowCircleUpIcon,
    className: "bg-slate-500/10 text-slate-600 ",
    label: "Portefeuille",
  },
  WITHDRAWAL_SUBMITTED: {
    icon: BankIcon,
    className: "bg-indigo-500/10 text-indigo-600 ",
    label: "Retrait",
  },
  WITHDRAWAL_APPROVED: {
    icon: CheckCircleIcon,
    className: "bg-emerald-500/10 text-emerald-600 ",
    label: "Retrait",
  },
  WITHDRAWAL_REFUSED: {
    icon: XCircleIcon,
    className: "bg-red-500/10 text-red-600 ",
    label: "Retrait",
  },
  REFERRAL_REGISTERED: {
    icon: UsersIcon,
    className: "bg-violet-500/10 text-violet-600 ",
    label: "Parrainage",
  },
  REFERRAL_REWARD_MONEY: {
    icon: GiftIcon,
    className: "bg-violet-500/10 text-violet-600 ",
    label: "Récompense",
  },
  REFERRAL_REWARD_SOUSCRIPTION: {
    icon: GiftIcon,
    className: "bg-violet-500/10 text-violet-600 ",
    label: "Récompense",
  },
  AMBASSADOR_COMMISSION_EARNED: {
    icon: PercentIcon,
    className: "bg-violet-500/10 text-violet-600 ",
    label: "Commission",
  },
  ATS_SCORE_READY: {
    icon: GaugeIcon,
    className: "bg-sky-500/10 text-sky-600 ",
    label: "IA",
  },
  TRANSLATION_READY: {
    icon: TranslateIcon,
    className: "bg-sky-500/10 text-sky-600 ",
    label: "IA",
  },
  PRINT_FAILED: {
    icon: PrinterIcon,
    className: "bg-amber-500/10 text-amber-600 ",
    label: "Export",
  },
  DOWNLOAD_READY: {
    icon: DownloadSimpleIcon,
    className: "bg-sky-500/10 text-sky-600 ",
    label: "Export",
  },
  WELCOME: { icon: HandWavingIcon, className: "bg-primary/10 text-primary", label: "Compte" },
  EMAIL_VERIFIED: {
    icon: EnvelopeSimpleIcon,
    className: "bg-emerald-500/10 text-emerald-600 ",
    label: "Compte",
  },
  PASSWORD_CHANGED: {
    icon: LockIcon,
    className: "bg-red-500/10 text-red-600 ",
    label: "Sécurité",
  },
  TWO_FACTOR_ENABLED: {
    icon: ShieldCheckIcon,
    className: "bg-emerald-500/10 text-emerald-600 ",
    label: "Sécurité",
  },
  TWO_FACTOR_DISABLED: {
    icon: ShieldWarningIcon,
    className: "bg-red-500/10 text-red-600 ",
    label: "Sécurité",
  },
  ACCOUNT_STATUS_CHANGED: {
    icon: UserCircleIcon,
    className: "bg-amber-500/10 text-amber-600 ",
    label: "Compte",
  },
  RESUME_VIEWED: {
    icon: EyeIcon,
    className: "bg-sky-500/10 text-sky-600 ",
    label: "CV",
  },
  RESUME_SHARED: {
    icon: ShareNetworkIcon,
    className: "bg-sky-500/10 text-sky-600 ",
    label: "CV",
  },
  SYSTEM_ANNOUNCEMENT: {
    icon: MegaphoneIcon,
    className: "bg-primary/10 text-primary",
    label: "Annonce",
  },
  ADMIN_WITHDRAWAL_REQUEST: {
    icon: BankIcon,
    className: "bg-amber-500/10 text-amber-600 ",
    label: "Admin",
  },
  ADMIN_PAYMENT_ALERT: {
    icon: CreditCardIcon,
    className: "bg-emerald-500/10 text-emerald-600 ",
    label: "Admin",
  },
  ADMIN_SIGNUP_ALERT: {
    icon: UserPlusIcon,
    className: "bg-sky-500/10 text-sky-600 ",
    label: "Admin",
  },
  HELP_REQUEST_OPENED: {
    icon: LifebuoyIcon,
    className: "bg-amber-500/10 text-amber-600 ",
    label: "Support",
  },
  HELP_REQUEST_REPLY: {
    icon: ChatsCircleIcon,
    className: "bg-emerald-500/10 text-emerald-600 ",
    label: "Support",
  },
  HELP_REQUEST_RESOLVED: {
    icon: CheckCircleIcon,
    className: "bg-emerald-500/10 text-emerald-600 ",
    label: "Support",
  },
};

export const getEventDisplay = (type: string): EventDisplay => EVENT_DISPLAY[type] ?? GENERIC;

export const CATEGORY_LABELS: Record<NotificationCategory, string> = {
  transactional: "Transactions",
  finance: "Finance",
  referral: "Parrainage",
  ai: "IA & Documents",
  account: "Compte & Sécurité",
  social: "Activité",
  system: "Système",
  campaign: "Communications",
  lifecycle: "Rappels et suivi",
};
